import React, { createContext, useContext, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  CssBaseline,
  ThemeProvider,
  Typography,
  Box,
} from "@mui/material";
import { createTheme } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import Dialog from "../components/Dialog";

const StateContext = createContext();

export const ContextProvider = ({ children }) => {
  const [snackTheme, setSnackTheme] = useState(createTheme());
  const [iconType, setIconType] = useState("check"); // Default icon type

  const [activeMenu, setActiveMenu] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [screenSize, setScreenSize] = useState(undefined);
  const [overlayName, setOverlayName] = useState(null);
  const [overlayData, setOverlayData] = useState(undefined);
  const [color, setColor] = useState(snackTheme.palette.background.default);
  const [customPageTitle, setCustomPageTitle] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [refreshPage, setRefreshPage] = useState(false);

  const triggerPageRefresh = (message, theme) => {
    setRefreshPage((prev) => !prev);
    if (message) {
      showSnackbar(message, theme, "success", theme.palette.background.default);
    }
  };

  const showSnackbar = (message, theme, iconType, color) => {
    setSnackbarMessage(message);
    setIconType(iconType);
    setSnackTheme(theme);
    setSnackbarOpen(true);

    if (color) {
      setColor(color);
    } else {
      // Use the theme's background.alt color for proper dark/light mode support
      setColor(theme.palette.background.default);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getIcon = (theme) => {
    switch (iconType) {
      case "error":
        return (
          <CancelRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.error.main,
              marginRight: "1rem",
            }}
          />
        );
      case "info":
        return (
          <HelpRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.info.main,
              marginRight: "1rem",
            }}
          />
        );
      default:
      case "success":
        return (
          <CheckRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.primary.main,
              marginRight: "1rem",
            }}
          />
        );
    }
  };

  // 🧩 Dialog state
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: "",
    message: "",
    customContent: null,
    primaryAction: null,
    secondaryAction: null,
    tertiaryAction: null,
    primaryOnClick: null,
    secondaryOnClick: null,
    tertiaryOnClick: null,
  });
  
  // 🪄 Function to open a dialog
  const openDialog = (config) => {
    setDialogConfig({
      theme: config.theme,
      open: true,
      title: config.title || "",
      message: config.message || "",
      customContent: config.customContent || null,
      primaryAction: config.primaryAction || null,
      secondaryAction: config.secondaryAction || null,
      tertiaryAction: config.tertiaryAction || null,
      primaryOnClick: (e) => {
        setDialogConfig((prev) => ({ ...prev, primaryDisabled: true }));
        if (config.primaryOnClick) config.primaryOnClick(e);
        else closeDialog();
      },
      secondaryOnClick: config.secondaryOnClick || closeDialog,
      tertiaryOnClick: config.tertiaryOnClick || closeDialog,
      primaryDisabled: false,
    });
  };

  // 🧼 Function to close dialog
  const closeDialog = () =>
    setDialogConfig((prev) => ({ ...prev, open: false, primaryDisabled: false }));

  // ⚠️ Helper for quick error dialogs
  const handleErrorDialog = (message, theme) => {
    openDialog({
      theme: theme,
      title: "Error",
      message,
      primaryAction: "OK",
      primaryOnClick: closeDialog,
    });
  };

  return (
    <ThemeProvider theme={snackTheme}>
      <CssBaseline />
      <StateContext.Provider
        value={{
          activeMenu,
          setActiveMenu,
          isCollapsed,
          setIsCollapsed,
          screenSize,
          setScreenSize,
          overlayName,
          setOverlayName,
          overlayData,
          setOverlayData,
          refreshPage,
          triggerPageRefresh,
          showSnackbar,
          openDialog,
          closeDialog,
          handleErrorDialog,
          customPageTitle,
          setCustomPageTitle,
        }}
      >
        {children}

        <Dialog
          theme={dialogConfig.theme}
          openDialog={dialogConfig.open}
          title={dialogConfig.title}
          errorMessage={dialogConfig.message}
          customContent={dialogConfig.customContent}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
          tertiaryAction={dialogConfig.tertiaryAction}
          primaryOnClick={dialogConfig.primaryOnClick}
          secondaryOnClick={dialogConfig.secondaryOnClick}
          tertiaryOnClick={dialogConfig.tertiaryOnClick}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Box
            display="flex"
            alignItems="center"
            p="1.2rem 2.2rem"
            bgcolor={color}
            boxShadow={snackTheme.shadows[3]}
            borderRadius="20px"
          >
            {getIcon(snackTheme)}
            <Typography variant="subtitle1" fontWeight="bold">
              {snackbarMessage}
            </Typography>
          </Box>
        </Snackbar>
      </StateContext.Provider>
    </ThemeProvider>
  );
};

export const useStateContext = () => useContext(StateContext);
